/** setTimeout overflows above 2^31-1 ms; longer waits wake early and plan again. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * One timer that wakes at the earliest planned time instead of on a fixed
 * tick. Each `schedule` replaces the pending wake-up.
 */
export class DeadlineTimer {
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly onWake: () => void) {}

  /** Wakes at the earliest time after `now`; none after `now` means no wake-up. */
  schedule(wakeups: readonly number[], now: number): void {
    this.clear();
    const future = wakeups.filter(at => at > now);
    if (future.length === 0) return;
    const delay = Math.min(Math.min(...future) - now, MAX_TIMER_DELAY_MS);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.onWake();
    }, delay);
  }

  clear(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}

/** Wait after failed attempts: the base time, doubling up to the maximum. */
export class RetryBackoff {
  private failures = 0;
  private blockedUntil = 0;

  constructor(
    private readonly baseMs: number,
    private readonly maxMs: number
  ) {}

  /** Earliest time of the next attempt; 0 when nothing failed. */
  get nextAttemptAt(): number {
    return this.blockedUntil;
  }

  failed(now: number): void {
    this.failures += 1;
    this.blockedUntil =
      now + Math.min(this.baseMs * 2 ** (this.failures - 1), this.maxMs);
  }

  reset(): void {
    this.failures = 0;
    this.blockedUntil = 0;
  }
}
